import {Component, OnDestroy, TemplateRef, ViewChild} from '@angular/core';
import {TableBaseComponent} from "@pages/table.base.component";
import {ClientPortalUser} from "@data/interefaces/client-portal.interfaces";
import {ActivatedRoute} from "@angular/router";
import {ClientPortalService} from "@data/client-portal/client-portal.service";
import {AgGridService} from "@shared/services/ag-grid.service";
import {FilterChangedEvent, SelectionChangedEvent, SelectionColumnDef} from "@ag-grid-community/core";
import {assignClickAction, pageContext, permanentClickAction, releaseClickAction, tableColumns, temporaryClickAction} from "./user-list.component.ds";
import {KpiSidebarItem} from "@shared/ag-kpi-sidebar/ag-kpi-sidebar.component.ds";
import {AgKpiSidebarUtil} from "@shared/ag-kpi-sidebar/ag-kpi-sidebar.util";
import {CommonProcessStatus, NotificationType, TableauLicenseRequestType} from "@data/enums/data.enums";
import {Util} from "@data/util/util";
import {FeatureComponentMode} from "@core/enums/core.enums";

@Component({
  selector: 'app-user-list',
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.scss'],
  providers: [AgGridService]
})
export class UserListComponent extends TableBaseComponent implements OnDestroy {
  rowData: ClientPortalUser[] = [];
  selectedRows: ClientPortalUser[] = [];
  kpiSidebar: KpiSidebarItem[] = [];
  refreshComplete: boolean = true;

  isFilterActive: boolean = false;
  isBulkOperationAllowed: boolean = false;
  selectedFeatureMode!: FeatureComponentMode;
  @ViewChild('footerActionButtonPanel') footerActionButtonPanel: TemplateRef<any> | undefined;

  showTaskComponent: boolean = false;

  protected readonly pageContext = pageContext;
  protected override readonly tableColumns = tableColumns
  protected kpiSidebarUtil = AgKpiSidebarUtil;

  private inProcessingLogins: string[] = [];
  private intervalId: any;

  constructor(private _activatedRoute: ActivatedRoute, private _clientPortalService: ClientPortalService) {
    super();
  }

  public static isUserRequestInProcessing(user: ClientPortalUser): boolean {
    return user && (user?.processStatus === CommonProcessStatus.PENDING || user?.processStatus === CommonProcessStatus.IN_PROGRESS);
  }

  public static couldAssignTableauLicense(user: ClientPortalUser): boolean {
    return user && !user?.hasTableauLicense;
  }

  public static couldReleaseTableauLicense(user: ClientPortalUser): boolean {
    return user && user?.hasTableauLicense;
  }

  public static couldMakeLicensePermanent(user: ClientPortalUser): boolean {
    return user && user?.hasTableauLicense && !user?.tableauLicenseIsPermanent;
  }

  public static couldMakeLicenseTemporary(user: ClientPortalUser): boolean {
    return user && user?.hasTableauLicense && user?.tableauLicenseIsPermanent;
  }

  // getRowId is marked as 'Initial', which means the value can not be updated after the grid has been created.
  // To fix this, we will set the getRowId directly in the HTML for the grid, and reference this function to override
  // the row ids value
  public getRowIdFn = (params: { data: any }) => {
    return params.data.login;
  };

  public override ngOnInit() {
    super.ngOnInit();
    this.initiatePageState(this._activatedRoute.snapshot.queryParamMap);
    this.refreshView();
    this.intervalId = setInterval(async () => {
      await this.fetchInProcessingUsers();
    }, 5000);
  }

  ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  // initGridOptions() {
  //   // const options = this._agGridService.getDefaultAgiGridOptions();
  //   // options.getRowId = (params) => params.data.login;
  //   // this.baseAgGridOptions = options;
  // }

  goBack() {
    this.showTaskComponent = false;
    this.refreshView();
  }

  onRowSelectionChanged(event: SelectionChangedEvent) {
    this.refreshGridHeaderOnSelectionChanged(event);
    this.selectedRows = this.getSelectedRows();
    this.isBulkOperationAllowed = this.selectedRows.length != 0;
  }

  onBulkProcessClicked() {
    this.selectedFeatureMode = FeatureComponentMode.BULK_LICENSE;
    this.showTaskComponent = true;
  }

  refreshView() {
    this.refreshComplete = false;
    this.displayProgressBar(true);
    this._clientPortalService.fetchAllUsers()
      .then(users => {
        this.rowData = users;
        this.refreshInProcessingLogins(users);
        return this.loadKpis();
      })
      .then(kpis => {
        this.kpiSidebar = this.enhanceKpisData(kpis);
        this.refreshCells();
      })
      .catch(error => this.displayNotificationMessage('error', JSON.stringify(error)))
      .finally(() => {
        this.displayProgressBar(false)
        this.refreshComplete = true;
      });
  }

  onFilterChanged(event: FilterChangedEvent<any>) {
    const updateLiveDataFilter = this.isUpdateLiveDataFilterRequired(event.columns);
    this._agGridService.processOnFilterChanged(event, this.pageContext, this.updatePageContext.bind(this)).then(() => {
      if (updateLiveDataFilter) {
        this.onLiveDataFilterChange();
      }
    });
  }

  onLiveDataFilterChange(): void {
    this.updateToPageContextUrl();
  }

  protected override initAfterGridReady() {
    this.gridApi.addGlobalListener((eventType: any, event: any) => {
      switch (eventType) {
        case assignClickAction: {
          this.sendTableauLicenseRequest(event.detail.rowData, TableauLicenseRequestType.Assign);
          break;
        }
        case releaseClickAction: {
          this.sendTableauLicenseRequest(event.detail.rowData, TableauLicenseRequestType.Release);
          break;
        }
        case permanentClickAction: {
          this.changeTableauLicenseType(event.detail.rowData, true);
          break;
        }
        case temporaryClickAction: {
          this.changeTableauLicenseType(event.detail.rowData, false);
          break;
        }
      }
    });
  }

  protected override getTableFooterActionButtonPanel(): TemplateRef<any> | undefined {
    return this.footerActionButtonPanel
  }

  protected getSelectionColumnDef(): SelectionColumnDef {
    return this.getSelectColumnDef((params: any) => {
      return {
        showFlag: params.data?.loadStatus == 'Error',
        flagIcon: "fa-exclamation",
        flagColor: "red",
        toolTip: 'Error'
      }
    });
  }

  protected updatePageContext(updateContextUrl: boolean = true): void {
    this.isFilterActive = Util.checkFilterActive(this.pageContext);
    if (updateContextUrl) {
      this.updateToPageContextUrl();
    }
  }

  private sendTableauLicenseRequest(user: ClientPortalUser, requestType: TableauLicenseRequestType) {
    if (user.login) {
      const message = TableauLicenseRequestType.Assign == requestType ?
        `Are you sure assign Tableau License to user ${user.login} ?` :
        `Are you sure release user ${user.login}'s Tableau License?`
      this.displayDialogMessage(NotificationType.WARNING, message, undefined, false)
        .then(result => {
          if (result) {
            this._clientPortalService.sendTableauLicenseRequest(user.login, requestType)
              .then(result => {
                Object.assign(user, result);
                user.processStatus = result.processStatus;
                user.processResult = result.processResult;
                if (UserListComponent.isUserRequestInProcessing(user)) {
                  this.inProcessingLogins.push(user.login);
                }
                this.refreshCells();
              })
              .catch(error => this.displayNotificationMessage('error', JSON.stringify(error)));
          }
        });
    }
  }

  private changeTableauLicenseType(user: ClientPortalUser, isPermanent: boolean) {
    if (user.login) {
      const message = isPermanent ?
        `Are you sure change user ${user.login}'s Tableau License to permanent ?` :
        `Are you sure change user ${user.login}'s Tableau License to temporary?`;
      this.displayDialogMessage(NotificationType.WARNING, message, undefined, false)
        .then(result => {
          if (result) {
            this._clientPortalService.changeTableauLicenseType(user.login, isPermanent)
              .then(result => {
                Object.assign(user, result);
                user.tableauLicenseIsPermanent = result.tableauLicenseIsPermanent;
                user.tableauLicenseExpiryDatetime = result.tableauLicenseExpiryDatetime;
                this.refreshCells();
                const info = isPermanent ?
                  `User ${user.login}'s Tableau License has changed to permanent.` :
                  `User ${user.login}'s Tableau License has changed to temporary.`;
                this.displayNotificationMessage("info", info);
              })
              .catch(error => this.displayNotificationMessage('error', JSON.stringify(error)));
          }
        });
    }
  }

  private refreshInProcessingLogins(users: ClientPortalUser[]) {
    this.inProcessingLogins = users
      .filter(user => user && UserListComponent.isUserRequestInProcessing(user))
      .map(user => user.login);
  }

  private async fetchInProcessingUsers() {
    if (this.inProcessingLogins.length > 0) {
      const results = await this._clientPortalService.fetchUsersByLogins(this.inProcessingLogins);
      const completedLogins: string[] = [];
      results.forEach(user => {
        this.updateRowData(user);
        if (!UserListComponent.isUserRequestInProcessing(user)) {
          completedLogins.push(user.login);
        }
      });
      this.refreshCells();
      if (completedLogins.length > 0) {
        this.inProcessingLogins = this.inProcessingLogins.filter(login => !completedLogins.includes(login));
        this.notifyRequestsCompletion(completedLogins);
        this.reloadKpis();
      }
    }
  }

  private reloadKpis() {
    this.loadKpis().then(kpis => this.kpiSidebar = this.enhanceKpisData(kpis))
      .catch(error => this.displayNotificationMessage('error', JSON.stringify(error)))
  }

  private loadKpis(): Promise<KpiSidebarItem[]> {
    return this._clientPortalService.tableauLicenseKpis();
  }

  private notifyRequestsCompletion(logins: string[]) {
    const loginList = logins.join(', ');
    const message = `The Tableau License requests for the following logins have been completed: ${loginList}.`;
    this.displayNotificationMessage("info", message);
  }

  private refreshCells() {
    this.gridApi.refreshCells({
      force: true
    });
    this.selectedRows = this.getSelectedRows();
    // this.gridApi.redrawRows();
  }

  private updateRowData(user: ClientPortalUser) {
    const rowNode = this.gridApi.getRowNode(user.login);
    if (rowNode) {
      rowNode.setData(user);
    }
  }

  private enhanceKpisData(kpiItems: KpiSidebarItem[]): KpiSidebarItem[] {
    return kpiItems.map(kpiItem => {
      return this.populateChartData(kpiItem);
    });
  }

  private populateChartData(kpiItem: KpiSidebarItem): KpiSidebarItem {
    const value = parseFloat(kpiItem.value.toString());
    const thresholds = isNaN(value) ? 0 : value;
    let remaining = thresholds > 100 ? 0 : 100 - thresholds;
    remaining = Math.round(remaining * 100) / 100;

    let color: string;
    if (thresholds < 75) {
      color = 'green';
    } else if (thresholds >= 75 && thresholds < 90) {
      color = 'yellow';
    } else {
      const overage = thresholds - 90;
      const red = Math.min(255, 180 + overage * 3);
      const green = Math.max(0, 75 - overage * 3);
      const blue = Math.max(0, 75 - overage * 3);
      color = `rgb(${red}, ${green}, ${blue})`;
    }
    const chartOptions = this.kpiSidebarUtil.defaultDonutOptions(
      [
        {category: "Thresholds", value: thresholds},
        {category: "Remaining", value: remaining}
      ],
      [color, "lightblue"],
      {text: `${thresholds}%`, color: color},
      (params: any) => `${params.datum.value}%`,
    );
    kpiItem.type = 'chart';
    kpiItem.chart = {options: chartOptions};
    return kpiItem;
  }
}
